import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDisplayTime, setErrorDisplayTime] = useState(null);
  const [loginStep, setLoginStep] = useState('form'); // 'form', 'authenticating', 'success'
  const [securityProgress, setSecurityProgress] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Only clear error after minimum display time (3 seconds) if user starts typing
    if (error && errorDisplayTime) {
      const timeSinceError = Date.now() - errorDisplayTime;
      if (timeSinceError > 3000) {
        setError('');
        setErrorDisplayTime(null);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const simulateSecurityChecks = () => {
    const checks = [
      { id: 1, text: 'Encrypting credentials...', delay: 800 },
      { id: 2, text: 'Verifying user identity...', delay: 1400 },
      { id: 3, text: 'Checking security protocols...', delay: 2000 },
      { id: 4, text: 'Establishing secure connection...', delay: 2600 },
      { id: 5, text: 'Authentication successful!', delay: 3200 }
    ];

    checks.forEach((check, index) => {
      setTimeout(() => {
        setSecurityProgress(prev => [...prev, check]);
        if (index === checks.length - 1) {
          setTimeout(() => {
            setLoginStep('success');
            // Redirect after success screen shows for a bit
            setTimeout(() => {
              navigate('/dashboard');
            }, 1500);
          }, 400);
        }
      }, check.delay);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorDisplayTime(null);
    
    try {
      // First verify credentials without showing animation
      const response = await authService.login(formData.email, formData.password);
      
      // Only show animation if credentials are valid
      setLoginStep('authenticating');
      setSecurityProgress([]);
      
      // Start security animation after successful login
      simulateSecurityChecks();
      
      // Login the user
      login(response.user);
      
    } catch (error) {
      // Show error immediately without animation
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      console.error('Error.error:', error.error);
      
      let errorMessage = 'Login failed. Please try again.';
      
      // Check for specific HTTP status codes first
      if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid email or password format. Please check your credentials.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      // Then check for specific error messages in the response
      else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } 
      // Check for error property directly on error object
      else if (error.error) {
        errorMessage = error.error;
      } 
      // Check for general message
      else if (error.message === 'Network Error') {
        errorMessage = 'Network connection failed. Please check your internet connection.';
      } else if (error.message && error.message !== 'Network Error') {
        errorMessage = error.message;
      }
      
      
      setError(errorMessage);
      setErrorDisplayTime(Date.now());
      setLoginStep('form');
      setSecurityProgress([]);
      
      // Ensure we stay on the login form
      if (window.location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    } finally {
      // Always reset loading state
      setLoading(false);
    }
  };

  const renderAuthenticating = () => (
    <div className="auth-loading-container">
      <div className="security-icons-ring">
        <div className="security-icon"><i className="fas fa-shield-alt"></i></div>
        <div className="security-icon"><i className="fas fa-lock"></i></div>
        <div className="security-icon"><i className="fas fa-key"></i></div>
        <div className="security-icon"><i className="fas fa-user-check"></i></div>
        <div className="security-icon"><i className="fas fa-fingerprint"></i></div>
        <div className="security-icon"><i className="fas fa-eye"></i></div>
        <div className="central-security-logo">
          <i className="fas fa-bolt"></i>
        </div>
      </div>
      
      <div className="auth-loading-text">Securing Your Access</div>
      <div className="auth-loading-subtext">
        Your credentials are being encrypted and verified through our secure authentication system
      </div>

      <div className="security-progress">
        {securityProgress.map((item) => (
          <div key={item.id} className="security-progress-item completed">
            <i className="fas fa-check-circle"></i>
            <span>{item.text}</span>
          </div>
        ))}
        {securityProgress.length < 5 && (
          <div className="security-progress-item active">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Processing...</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="auth-success-container">
      <div className="auth-success-icon">
        <i className="fas fa-check"></i>
      </div>
      <div className="auth-success-text">Login Successful!</div>
      <div className="auth-success-subtext">Redirecting to your dashboard...</div>
      
      <div className="security-features">
        <div className="security-feature">
          <i className="fas fa-shield-alt"></i>
          <span>End-to-End Encryption</span>
        </div>
        <div className="security-feature">
          <i className="fas fa-lock"></i>
          <span>Secure Session</span>
        </div>
        <div className="security-feature">
          <i className="fas fa-user-shield"></i>
          <span>Identity Verified</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="auth-page">
      <div className="form-container">
        {loginStep === 'form' && (
          <>
            <div className="auth-header">
              <h2>Login</h2>
              <p>Sign in to your account</p>
            </div>
            
            {error && (
              <div className="alert alert-error" style={{ 
                marginBottom: '1.5rem', 
                display: 'block',
                animation: 'authFadeIn 0.3s ease-in',
                border: '1px solid #ff6b6b',
                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                borderRadius: '8px',
                padding: '12px 16px',
                fontWeight: '500'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ 
                  marginRight: '8px', 
                  color: '#ff6b6b' 
                }}></i>
                <span style={{ color: '#ff6b6b' }}>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email Address:</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={togglePasswordVisibility}
                    disabled={loading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className={`btn btn-primary auth-submit-btn ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="auth-links">
              <Link to="/register" className="auth-link primary">
                Don't have an account? <span>Sign Up</span>
              </Link>
              <Link to="/forgot-password" className="auth-link secondary">
                Forgot your password?
              </Link>
            </div>
          </>
        )}

        {loginStep === 'authenticating' && renderAuthenticating()}
        {loginStep === 'success' && renderSuccess()}
      </div>
    </div>
  );
};

export default Login;